import type { RegionalSeasonConfig, Season } from './types';

/**
 * Default regional season mapping (Northern Hemisphere)
 */
const DEFAULT_REGIONAL_CONFIG: RegionalSeasonConfig = {
    spring: [[3, 5]], // March - May
    summer: [[6, 8]], // June - August
    autumn: [[9, 11]], // September - November
    winter: [[12, 2]], // December - February
};

/**
 * Detect the current season based on date and regional configuration
 */
export function detectSeason(
    date: Date = new Date(),
    config: RegionalSeasonConfig = DEFAULT_REGIONAL_CONFIG
): Season {
    const month = date.getMonth() + 1; // 1-12

    for (const [season, ranges] of Object.entries(config) as [Season, [number, number][]][]) {
        for (const [start, end] of ranges) {
            // Handle year wrap-around (e.g., Dec - Feb for winter)
            if (start > end) {
                if (month >= start || month <= end) {
                    return season;
                }
            } else {
                if (month >= start && month <= end) {
                    return season;
                }
            }
        }
    }

    // Default fallback
    return 'winter';
}

/**
 * Check if the user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
    if (typeof window === 'undefined') return true;
    if (!window.matchMedia) return false;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    return mediaQuery.matches;
}

/**
 * Detect if the current device is mobile
 */
export function isMobileDevice(): boolean {
    if (typeof window === 'undefined') return false;
    if (typeof navigator === 'undefined') return false;

    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
    );
}

/**
 * Create a canvas element optimized for the target
 */
export function createCanvas(_target: HTMLElement): HTMLCanvasElement {
    const canvas = document.createElement('canvas');

    // Style canvas to overlay the target
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '9999';

    return canvas;
}

/**
 * Update canvas size to match its container
 */
export function resizeCanvas(canvas: HTMLCanvasElement, container: HTMLElement): void {
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
}

/**
 * FPS throttler utility
 */
export class FPSThrottler {
    private lastTime = 0;
    private interval: number;

    constructor(maxFPS: number = 60) {
        this.interval = 1000 / maxFPS;
    }

    shouldRender(currentTime: number): boolean {
        if (currentTime - this.lastTime >= this.interval) {
            this.lastTime = currentTime;
            return true;
        }
        return false;
    }

    setMaxFPS(fps: number): void {
        this.interval = 1000 / fps;
    }
}
